import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  $applyNodeReplacement,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  type LexicalNode,
  type NodeKey,
  type Spread,
  DecoratorNode,
  LexicalEditor,
  isHTMLElement,
  SerializedLexicalNode,
} from 'lexical'
import { CrewEntityReference } from 'components/elements/crewEntityReference/crewEntityReference'
import { EntityType } from '@crew/enums/domain'
import { entityReference as entityReferenceConst } from '@crew/configs/constants'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection'
import { mergeRegister } from '@lexical/utils'
import { FC, memo, useCallback, useEffect, useRef } from 'react'

type EntityReferenceComponentProps = {
  entityType: EntityType
  entityRecordId: string
  nodeKey: NodeKey
}

// エディタ上の参照はクリックでNodeSelectionに載せ、Delete/Backspaceで要素ごと削除できるようにする
const EntityReferenceComponent: FC<EntityReferenceComponentProps> = memo(
  (props) => {
    const [editor] = useLexicalComposerContext()
    const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(
      props.nodeKey
    )
    const containerRef = useRef<HTMLSpanElement | null>(null)

    const handleDelete = useCallback(
      (event: KeyboardEvent) => {
        // この参照自体が選択されているときだけ削除キーを横取りする
        if (!isSelected) {
          return false
        }

        const selection = $getSelection()
        if (!$isNodeSelection(selection)) {
          return false
        }

        event.preventDefault()
        const node = $getNodeByKey(props.nodeKey)
        if (!$isEntityReferenceNode(node)) {
          return false
        }

        node.remove()
        return true
      },
      [isSelected, props.nodeKey]
    )

    useEffect(() => {
      return mergeRegister(
        editor.registerCommand(
          CLICK_COMMAND,
          (event: MouseEvent) => {
            const target = event.target
            if (
              !(target instanceof Node) ||
              !containerRef.current?.contains(target)
            ) {
              return false
            }

            // 単体クリック時はこの参照のみを選択し、Shift+クリック時は選択状態を切り替える
            if (event.shiftKey) {
              setSelected(!isSelected)
            } else {
              clearSelection()
              setSelected(true)
            }

            return true
          },
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(
          KEY_BACKSPACE_COMMAND,
          handleDelete,
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(
          KEY_DELETE_COMMAND,
          handleDelete,
          COMMAND_PRIORITY_LOW
        )
      )
    }, [clearSelection, editor, handleDelete, isSelected, setSelected])

    return (
      <span
        ref={containerRef}
        className={isSelected ? 'rounded-sm outline outline-crew-blue-200' : ''}
      >
        <CrewEntityReference
          entityType={props.entityType}
          entityRecordId={props.entityRecordId}
          isLinkEnabled={false}
        />
      </span>
    )
  }
)

// シリアライズ用型の定義
// editorState.toJSON() で EntityReferenceNode がどう出てくるかの型
export type SerializedEntityReferenceNode = Spread<
  {
    type: typeof entityReferenceConst.CLASS_NAME
    entityType: EntityType
    entityRecordId: string
    entityName: string
  },
  SerializedLexicalNode
>

// EntityReferenceNode クラスの実装
export class EntityReferenceNode extends DecoratorNode<JSX.Element> {
  __entityType: EntityType
  __entityRecordId: string
  __entityName: string

  constructor(
    entityType: EntityType,
    entityRecordId: string,
    entityName: string,
    key?: NodeKey
  ) {
    super(key)
    this.__entityType = entityType
    this.__entityRecordId = entityRecordId
    this.__entityName = entityName
  }

  // ノードの種類を返すメソッド
  static getType(): string {
    return entityReferenceConst.CLASS_NAME
  }

  // ノードを複製するメソッド
  static clone(node: EntityReferenceNode): EntityReferenceNode {
    return new EntityReferenceNode(
      node.__entityType,
      node.__entityRecordId,
      node.__entityName,
      node.__key
    )
  }

  // JSON からノードを復元するためのメソッド
  static importJSON(
    serializedNode: SerializedEntityReferenceNode
  ): EntityReferenceNode {
    // text プロパティは SerializedTextNode 由来と仮定
    const node = new EntityReferenceNode(
      serializedNode.entityType,
      serializedNode.entityRecordId,
      serializedNode.entityName
    )
    return node
  }

  // シリアライズ処理
  exportJSON(): SerializedEntityReferenceNode {
    return {
      ...super.exportJSON(),
      entityType: this.__entityType,
      entityRecordId: this.__entityRecordId,
      entityName: this.__entityName,
    } as SerializedEntityReferenceNode
  }

  // ノードの DOM 表現を作成するメソッド
  createDOM(config: EditorConfig): HTMLElement {
    const dom = document.createElement('span')
    if (config.theme.reference) dom.className = config.theme.reference

    // Mobile側で編集時にclass指定がないと描画のスタイルをハンドリングできないので、DB上にも登録しておく必要がある
    dom.classList.add(entityReferenceConst.CLASS_NAME)

    return dom
  }

  updateDOM(): false {
    return false
  }

  // DOM 出力など必要に応じたメソッド（例として placeholder）
  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const { element } = super.exportDOM(editor)
    if (element && isHTMLElement(element)) {
      element.setAttribute(
        entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.NAME,
        entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.VALUE
      )
      element.setAttribute(
        entityReferenceConst.DATA_ATTRIBUTE.ENTITY_TYPE.NAME,
        this.__entityType
      )
      element.setAttribute(
        entityReferenceConst.DATA_ATTRIBUTE.ENTITY_RECORD_ID.NAME,
        this.__entityRecordId
      )
      // Mobile側で編集時にclass指定がないと描画のスタイルをハンドリングできないので、DB上にも登録しておく必要がある
      element.classList.add(entityReferenceConst.CLASS_NAME)

      element.textContent = this.__entityName
    }

    return { element }
  }

  // DOM から ReferenceNode への変換マップを提供するメソッド
  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (
          domNode.hasAttribute(
            entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.NAME
          )
        ) {
          return {
            conversion: $convertEntityReferenceElement,
            // 他の変換よりもこちらを優先する
            // prioが1だと、extendedTextNodeに変換されてしまう
            priority: 2,
          }
        }
        return null
      },
      // CrewHtmlContent（編集時でもないチャットメッセージの表示など）で、<span data-tag-type="reference" data-entity-type="Event" data-entity-record-id="123">が、<a>タグに変換されるため、それのペーストに対応するためにaタグの変換も追加する
      a: (domNode: HTMLElement) => {
        if (
          domNode.hasAttribute(
            entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.NAME
          )
        ) {
          return {
            conversion: $convertEntityReferenceElement,
            priority: 2,
          }
        }
        return null
      },
    }
  }

  decorate(): JSX.Element {
    return (
      <EntityReferenceComponent
        entityType={this.__entityType}
        entityRecordId={this.__entityRecordId}
        nodeKey={this.__key}
      />
    )
  }

  // テキストの途中に差し込まれる参照なので、inline要素として扱う
  isInline(): true {
    return true
  }

  // キーボード操作でNodeSelectionの対象にできるようにする
  isKeyboardSelectable(): true {
    return true
  }

  getTextContent(): string {
    return this.__entityName
  }
}

// DOM 要素を EntityReferenceNode に変換する関数
function $convertEntityReferenceElement(
  domNode: HTMLElement
): DOMConversionOutput | null {
  if (
    !domNode.hasAttribute(entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.NAME)
  ) {
    return null
  }
  if (
    !domNode.hasAttribute(entityReferenceConst.DATA_ATTRIBUTE.ENTITY_TYPE.NAME)
  ) {
    return null
  }
  if (
    !domNode.hasAttribute(
      entityReferenceConst.DATA_ATTRIBUTE.ENTITY_RECORD_ID.NAME
    )
  ) {
    return null
  }

  const entityType = domNode.getAttribute(
    entityReferenceConst.DATA_ATTRIBUTE.ENTITY_TYPE.NAME
  ) as EntityType
  const entityRecordId = domNode.getAttribute(
    entityReferenceConst.DATA_ATTRIBUTE.ENTITY_RECORD_ID.NAME
  )
  const entityName = domNode.textContent ?? ''

  if (entityType && entityRecordId) {
    const node = $createEntityReferenceNode(
      entityType,
      entityRecordId,
      entityName
    )
    return { node }
  }

  return null
}

// 新しい ReferenceNode を作成する関数
export function $createEntityReferenceNode(
  entityType: EntityType,
  entityRecordId: string,
  entityName: string = `!${entityType}:${entityRecordId}`
): EntityReferenceNode {
  const referenceNode = new EntityReferenceNode(
    entityType,
    entityRecordId,
    entityName
  )
  return $applyNodeReplacement(referenceNode)
}

// 与えられたノードが EntityReferenceNode かどうかを判定する関数
export function $isEntityReferenceNode(
  node: LexicalNode | null | undefined
): node is EntityReferenceNode {
  return node instanceof EntityReferenceNode
}
